import { useState } from 'react';
import config from '@app/config';
import { distinctValues } from '@app/utils/arrayUtils';

export const MAX_CHANGES_PER_SAVE = config.backee?.maxChangesPerSave ?? 100;

export interface StagedChanges {
  activeIds: string[];
  addedIds: string[];
  removedIds: string[];
  changedIds: string[];
  hasChanges: boolean;
  maxChanges: number;
  exceedsMaxChanges: boolean;
  discard(): void;
}

export interface CycleOptions {
  // Never stages an add, so the id cycles between its saved state and inactive
  removalOnly?: boolean;
}

export interface StagedMixedIds extends StagedChanges {
  indeterminateIds: string[];
  cycle(id: string, options?: CycleOptions): void;
  stage(id: string, active: boolean): void;
}

type IdState = 'active' | 'inactive' | 'indeterminate';

// Stages ids shared by several saved sets (e.g. the roles of several users); an id held by only some of them is indeterminate
export default function useStagedMixedIds(savedIdSets: string[][]): StagedMixedIds {
  const [stagedStates, setStagedStates] = useState<Record<string, IdState>>({});

  // Keyed by content, since a caller may pass new arrays with the same ids on every render
  const savedIdSetsKey = JSON.stringify(savedIdSets.map((savedIds) => [...savedIds].sort()));
  const [stagedOnSavedIdSetsKey, setStagedOnSavedIdSetsKey] = useState(savedIdSetsKey);
  if (stagedOnSavedIdSetsKey !== savedIdSetsKey) {
    setStagedOnSavedIdSetsKey(savedIdSetsKey);
    setStagedStates({});
  }

  const holderCountById = new Map<string, number>();
  savedIdSets.forEach((savedIds) =>
    distinctValues(savedIds).forEach((id) => holderCountById.set(id, (holderCountById.get(id) ?? 0) + 1)),
  );

  function getSavedState(id: string): IdState {
    const holderCount = holderCountById.get(id) ?? 0;
    if (!holderCount) {
      return 'inactive';
    }

    return holderCount === savedIdSets.length ? 'active' : 'indeterminate';
  }

  function getState(id: string): IdState {
    return stagedStates[id] ?? getSavedState(id);
  }

  const allIds = distinctValues([...holderCountById.keys(), ...Object.keys(stagedStates)]);
  const addedIds = allIds.filter((id) => getState(id) === 'active' && getSavedState(id) !== 'active');
  const removedIds = allIds.filter((id) => getState(id) === 'inactive' && getSavedState(id) !== 'inactive');

  return {
    activeIds: allIds.filter((id) => getState(id) === 'active'),
    indeterminateIds: allIds.filter((id) => getState(id) === 'indeterminate'),
    addedIds,
    removedIds,
    changedIds: [...addedIds, ...removedIds],
    hasChanges: addedIds.length > 0 || removedIds.length > 0,
    maxChanges: MAX_CHANGES_PER_SAVE,
    exceedsMaxChanges: addedIds.length + removedIds.length > MAX_CHANGES_PER_SAVE,
    cycle: (id, options): void =>
      setStagedStates((currentStagedStates) => {
        const savedState = getSavedState(id);
        return withStagedState(
          currentStagedStates,
          id,
          getNextState(currentStagedStates[id] ?? savedState, savedState, !!options?.removalOnly),
          savedState,
        );
      }),
    stage: (id, active): void =>
      setStagedStates((currentStagedStates) =>
        withStagedState(currentStagedStates, id, active ? 'active' : 'inactive', getSavedState(id)),
      ),
    discard: (): void => setStagedStates({}),
  };
}

function withStagedState(
  stagedStates: Record<string, IdState>,
  id: string,
  state: IdState,
  savedState: IdState,
): Record<string, IdState> {
  const otherStagedStates = Object.fromEntries(Object.entries(stagedStates).filter(([stagedId]) => stagedId !== id));
  return state === savedState ? otherStagedStates : { ...otherStagedStates, [id]: state };
}

function getNextState(state: IdState, savedState: IdState, removalOnly: boolean): IdState {
  if (removalOnly) {
    return state === 'inactive' ? savedState : 'inactive';
  }

  switch (state) {
    case 'indeterminate':
      return 'active';
    case 'active':
      return 'inactive';
    case 'inactive':
      return savedState === 'indeterminate' ? 'indeterminate' : 'active';
  }
}
